import * as React from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ArrowDown, ArrowUp, ChevronsUpDown, Filter } from 'lucide-react';
import { cn } from '@/lib/utils';
import { FiltroDeColuna } from './FiltroDeColuna';

/**
 * Grade de relatório: cabeçalho fixo, ordenação por coluna, linhas
 * virtualizadas e clique na linha.
 *
 * Existe separada da TabelaEstoque porque os relatórios têm colunas próprias e
 * precisam aguentar dezenas de milhares de linhas — virtualizar é obrigatório,
 * não enfeite.
 */
export interface ColunaGrade<T> {
  chave: string;
  cabecalho: string;
  /** Largura em px. A coluna marcada com `elastica` ocupa o espaço que sobra. */
  largura: number;
  elastica?: boolean;
  alinhar?: 'dir';
  render: (linha: T) => React.ReactNode;
  /** Valor usado para ordenar. Ausente = coluna não ordenável. */
  ordenar?: (linha: T) => string | number;
  /**
   * Texto da célula para o filtro estilo Excel (lista de valores com
   * checkbox). Ausente = coluna sem filtro.
   */
  filtrar?: (linha: T) => string;
  /** Total da coluna (soma, contagem…) na linha fixa do pé. Nenhuma coluna com rodapé = sem a linha. */
  rodape?: React.ReactNode;
}

/** Valores marcados por coluna. Coluna ausente = sem filtro. */
export type FiltrosGrade = Record<string, string[]>;

/**
 * Aplica os filtros por coluna. `exceto` ignora o filtro de uma coluna — é
 * assim que o Excel monta a lista de valores dela: a partir do que os OUTROS
 * filtros deixaram.
 */
export function aplicarFiltros<T>(
  linhas: readonly T[],
  colunas: readonly ColunaGrade<T>[],
  filtros: FiltrosGrade,
  exceto?: string,
): T[] {
  const ativos = colunas
    .filter((c) => c.filtrar && c.chave !== exceto && filtros[c.chave])
    .map((c) => ({ extrair: c.filtrar!, aceitos: new Set(filtros[c.chave]) }));
  if (ativos.length === 0) return [...linhas];
  return linhas.filter((l) => ativos.every((f) => f.aceitos.has(f.extrair(l))));
}

const ALTURA_LINHA = 40;

export function GradeRelatorio<T>({
  linhas,
  colunas,
  chaveDe,
  aoClicar,
  selecionada,
  className,
  filtros,
  aoFiltrar,
  base,
  preencherTela = false,
}: {
  /**
   * A grade vai até o pé da janela e a PÁGINA não rola: um scroll só, o da
   * grade, com a barra horizontal sempre à vista. Sem isso, com altura fixa,
   * a página também rolava e a barra horizontal ficava escondida lá embaixo.
   */
  preencherTela?: boolean | undefined;
  /** As linhas a mostrar (já filtradas por quem chama, se houver filtro). */
  linhas: readonly T[];
  colunas: readonly ColunaGrade<T>[];
  /** Filtros por coluna, controlados por quem chama (totais e Excel usam o mesmo recorte). */
  filtros?: FiltrosGrade | undefined;
  aoFiltrar?: ((chave: string, valores: string[] | null) => void) | undefined;
  /** Linhas antes dos filtros por coluna — de onde sai a lista de valores. */
  base?: readonly T[] | undefined;
  chaveDe: (linha: T) => string;
  aoClicar?: ((linha: T) => void) | undefined;
  selecionada?: string | null | undefined;
  className?: string | undefined;
}): JSX.Element {
  const [ordem, definirOrdem] = React.useState<{ chave: string; desc: boolean } | null>(null);
  const [filtroAberto, definirFiltroAberto] = React.useState<{ chave: string; ancora: DOMRect } | null>(null);
  const comFiltro = aoFiltrar !== undefined;
  const container = React.useRef<HTMLDivElement | null>(null);
  const moldura = React.useRef<HTMLDivElement | null>(null);
  const [altura, definirAltura] = React.useState<number | null>(null);
  React.useLayoutEffect(() => {
    if (!preencherTela) return;
    const medir = (): void => {
      const el = moldura.current;
      if (!el) return;
      // Quem rola é o <main> do AppShell, não a janela. A conta: a altura que
      // faz o conteúdo dele caber sem rolar = área visível − o que está acima
      // da grade − o que está abaixo dela (paddings, rodapé da página).
      const rolagem = (el.closest('main') as HTMLElement | null) ?? (document.scrollingElement as HTMLElement);
      const caixa = rolagem.getBoundingClientRect();
      const acima = el.getBoundingClientRect().top - caixa.top + rolagem.scrollTop;
      // O que vem abaixo é medido pelo FIM do conteúdo da página, não pelo
      // scrollHeight: com pouca linha, o vazio do <main> entraria na conta e a
      // grade encolheria a cada medida.
      const pagina = rolagem.firstElementChild as HTMLElement | null;
      const fimPagina = pagina ? pagina.getBoundingClientRect().bottom - caixa.top + rolagem.scrollTop : acima + el.offsetHeight;
      const folgaMain = parseFloat(getComputedStyle(rolagem).paddingBottom) || 0;
      const abaixo = Math.max(0, fimPagina - acima - el.offsetHeight) + folgaMain;
      definirAltura(Math.max(280, Math.floor(rolagem.clientHeight - acima - abaixo - 12)));
    };
    medir();
    window.addEventListener('resize', medir);
    // O que fica acima (filtros, totais) muda de altura: mede de novo.
    const obs = new ResizeObserver(medir);
    if (moldura.current?.parentElement) obs.observe(moldura.current.parentElement);
    return () => {
      window.removeEventListener('resize', medir);
      obs.disconnect();
    };
  }, [preencherTela]);

  const ordenadas = React.useMemo(() => {
    if (!ordem) return linhas;
    const coluna = colunas.find((c) => c.chave === ordem.chave);
    if (!coluna?.ordenar) return linhas;
    const extrair = coluna.ordenar;
    return [...linhas].sort((a, b) => {
      const va = extrair(a);
      const vb = extrair(b);
      const cmp =
        typeof va === 'number' && typeof vb === 'number'
          ? va - vb
          : String(va).localeCompare(String(vb), 'pt-BR', { numeric: true });
      return ordem.desc ? -cmp : cmp;
    });
  }, [linhas, colunas, ordem]);

  const virtualizador = useVirtualizer({
    count: ordenadas.length,
    getScrollElement: () => container.current,
    estimateSize: () => ALTURA_LINHA,
    overscan: 14,
  });

  const grade = colunas
    .map((c) => (c.elastica ? `minmax(${c.largura}px,1fr)` : `${c.largura}px`))
    .join(' ');

  return (
    <div
      ref={moldura}
      className={cn(
        'flex min-h-0 flex-col overflow-hidden rounded-2xl border border-borda bg-superficie shadow-cartao',
        className,
      )}
      style={preencherTela && altura !== null ? { height: altura } : undefined}
    >
      <div className="flex min-h-0 flex-1 flex-col overflow-x-auto">
        <div role="table" className="flex min-h-0 w-full min-w-max flex-1 flex-col">
          <div
            role="row"
            className="grid shrink-0 items-center border-b border-borda bg-superficie2"
            style={{ gridTemplateColumns: grade }}
          >
            {colunas.map((coluna) => {
              const ativa = ordem?.chave === coluna.chave;
              return (
                <div role="columnheader" key={coluna.chave} className="group relative flex items-center">
                  <button
                    type="button"
                    disabled={!coluna.ordenar}
                    onClick={() =>
                      definirOrdem((atual) =>
                        atual?.chave === coluna.chave
                          ? { chave: coluna.chave, desc: !atual.desc }
                          : { chave: coluna.chave, desc: false },
                      )
                    }
                    className={cn(
                      'flex min-h-[42px] w-full min-w-0 items-center gap-1 px-3 text-[11px] font-bold uppercase tracking-[0.04em] transition-colors',
                      comFiltro && coluna.filtrar && 'pr-8',
                      coluna.alinhar === 'dir' ? 'justify-end' : 'text-left',
                      ativa ? 'text-primaria' : 'text-textoFraco',
                      coluna.ordenar ? 'hover:text-primaria' : 'cursor-default',
                    )}
                  >
                    {coluna.cabecalho}
                    {coluna.ordenar ? (
                      ativa ? (
                        ordem.desc ? (
                          <ArrowDown className="h-3 w-3" />
                        ) : (
                          <ArrowUp className="h-3 w-3" />
                        )
                      ) : (
                        <ChevronsUpDown className="h-3 w-3 opacity-30" />
                      )
                    ) : null}
                  </button>
                  {comFiltro && coluna.filtrar ? (
                    <button
                      type="button"
                      aria-label={`Filtrar ${coluna.cabecalho}`}
                      onClick={(e) =>
                        definirFiltroAberto({ chave: coluna.chave, ancora: e.currentTarget.getBoundingClientRect() })
                      }
                      className={cn(
                        'absolute right-1.5 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-md transition-colors',
                        filtros?.[coluna.chave]
                          ? 'bg-primaria text-white'
                          : 'text-textoSuave opacity-60 hover:bg-superficie3 hover:text-texto group-hover:opacity-100',
                      )}
                    >
                      <Filter className="h-3.5 w-3.5" />
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>

          <div ref={container} role="rowgroup" className="min-h-0 flex-1 overflow-y-auto">
            <div style={{ height: virtualizador.getTotalSize(), position: 'relative' }}>
              {virtualizador.getVirtualItems().map((item) => {
                const linha = ordenadas[item.index];
                if (linha === undefined) return null;
                const chave = chaveDe(linha);
                const ativa = selecionada === chave;
                return (
                  <div
                    role="row"
                    key={chave}
                    tabIndex={aoClicar ? 0 : -1}
                    onClick={() => aoClicar?.(linha)}
                    onKeyDown={(e) => {
                      if (aoClicar && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        aoClicar(linha);
                      }
                    }}
                    className={cn(
                      'absolute inset-x-0 grid items-center border-b border-borda text-[13px] transition-colors',
                      item.index % 2 === 1 && 'bg-superficie2',
                      aoClicar && 'cursor-pointer hover:bg-primaria-suave/60',
                      ativa && 'bg-primaria-suave shadow-[inset_3px_0_0_rgb(var(--cor-primaria))]',
                    )}
                    style={{
                      height: item.size,
                      transform: `translateY(${item.start}px)`,
                      gridTemplateColumns: grade,
                    }}
                  >
                    {colunas.map((coluna) => (
                      <div
                        role="cell"
                        key={coluna.chave}
                        className={cn(
                          'truncate px-3',
                          coluna.alinhar === 'dir' && 'tabular text-right',
                        )}
                      >
                        {coluna.render(linha)}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Dentro da tabela, não abaixo dela: rola na horizontal junto com as colunas. */}
          {colunas.some((c) => c.rodape !== undefined) ? (
            <div
              role="row"
              className="grid shrink-0 items-center border-t-2 border-borda bg-superficie2 text-[12.5px] font-bold"
              style={{ gridTemplateColumns: grade }}
            >
              {colunas.map((coluna) => (
                <div
                  role="cell"
                  key={coluna.chave}
                  className={cn('min-h-[38px] truncate px-3 py-2', coluna.alinhar === 'dir' && 'tabular text-right')}
                >
                  {coluna.rodape}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>
      {filtroAberto && aoFiltrar
        ? (() => {
            const coluna = colunas.find((c) => c.chave === filtroAberto.chave);
            if (!coluna?.filtrar) return null;
            return (
              <FiltroDeColuna
                titulo={coluna.cabecalho}
                ancora={filtroAberto.ancora}
                linhas={aplicarFiltros(base ?? linhas, colunas, filtros ?? {}, coluna.chave)}
                extrair={coluna.filtrar}
                marcados={filtros?.[coluna.chave] ?? null}
                aoAplicar={(v) => aoFiltrar(coluna.chave, v)}
                aoOrdenar={
                  coluna.ordenar ? (desc) => definirOrdem({ chave: coluna.chave, desc }) : undefined
                }
                aoFechar={() => definirFiltroAberto(null)}
              />
            );
          })()
        : null}
    </div>
  );
}
